/*
 * Reducer to handle the management and content of folders.
 */

import {createReducer} from '@reduxjs/toolkit';
import {DateTime} from 'luxon';

import {
  clearActiveFolder,
  deselectFolderViewItems,
  deselectFolderViewResults,
  loadFolderContents,
  resetFolderSelections,
  selectFolderViewItems,
  selectFolderViewResults,
  setActiveFolder,
  setActiveResult,
} from '../actions/folders.js';
import {resetTour} from '../actions/tour.js';
import {TOURS} from '@explorer/constants';
import {keyByDateAndType} from '@explorer/util/folders';

const DEFAULT_ASYNC_STATE = {
  error: null,
  pending: false,
  success: false,
};

export const defaultState = {
  selectedFolderId: '',
  folderContentsById: {},
  folderContentsNext: null,
  contentsPage: 1,

  loadFolderContents: DEFAULT_ASYNC_STATE,
  activeResult: null,

  viewResults: [],
  viewItems: [],
};

const foldersReducer = createReducer(defaultState, builder => {
  builder.addCase(setActiveFolder, (state, {payload}) => {
    state.selectedFolderId = payload;
  });

  builder.addCase(clearActiveFolder, () => defaultState);

  builder.addCase(resetTour, (state, {payload}) => {
    if (payload !== TOURS.OVERZOOM) {
      return defaultState;
    }
    return state;
  });

  builder.addCase(loadFolderContents.pending, (state, {meta}) => {
    // If loadFolderContents action is called with a string (folder Id) then we are loading a 'fresh' folder's contents
    // Otherwise, we are 'loading more' (fetching the next page of contents for the folder
    const loadMore = typeof meta.arg === 'string' ? false : true;

    // Folder state
    state.selectedFolderId =
      meta.arg && loadMore ? meta.arg.id : meta.arg || '';
    state.folderContentsNext = defaultState.folderContentsNext;
    state.contentsPage = loadMore
      ? state.contentsPage + 1
      : defaultState.contentsPage;

    state.loadFolderContents = {
      ...defaultState.loadFolderContents,
      pending: true,
    };
  });

  builder.addCase(loadFolderContents.fulfilled, (state, {payload}) => {
    const results = state.folderContentsById;

    payload?.features.forEach(feature => {
      const resultId = keyByDateAndType(feature);
      const acquired = DateTime.fromISO(feature.properties.acquired, {
        zone: 'utc',
      });

      if (feature.geometry) {
        if (!results[resultId]) {
          results[resultId] = {
            id: resultId,
            properties: {
              item_type: feature.properties.item_type,
              acquired: feature.properties.acquired,
              items: [feature],
            },
          };
        } else {
          const result = results[resultId];
          if (result.properties.items.find(item => item.id !== feature.id)) {
            result.properties.items.push(feature);
            if (state.viewResults.includes(result.id)) {
              state.viewItems.push(feature.id);
            }
          }
          if (
            acquired >
            DateTime.fromISO(result.properties.acquired, {zone: 'utc'})
          ) {
            result.properties.published = acquired.toISO();
          }
        }
      }
    });

    const viewResults = Object.values(results)
      .filter(result =>
        result.properties.items.some(item => state.viewItems.includes(item.id))
      )
      .map(r => r.id);

    state.folderContentsById = results;
    state.folderContentsNext = payload?._links?.next;
    state.loadFolderContents = {
      ...defaultState.loadFolderContents,
      success: true,
    };
    state.viewResults = viewResults;
  });

  builder.addCase(loadFolderContents.rejected, (state, {error}) => {
    state.loadFolderContents = {
      ...defaultState.loadFolderContents,
      error: error.message,
    };
  });

  builder.addCase(setActiveResult, (state, {payload}) => {
    state.activeResult = payload;
  });

  builder.addCase(
    selectFolderViewResults,
    (state, {payload: {reset, resultId}}) => {
      const resultItems =
        state.folderContentsById[resultId]?.properties.items.map(
          item => item.id
        ) || [];

      if (reset) {
        state.viewResults = [resultId];
        state.viewItems = [...resultItems];
      } else {
        state.viewResults = Array.from(
          new Set([...state.viewResults, resultId])
        );
        state.viewItems = Array.from(
          new Set([...state.viewItems, ...resultItems])
        );
      }
    }
  );

  builder.addCase(deselectFolderViewResults, (state, {payload: {resultId}}) => {
    const resultItems =
      state.folderContentsById[resultId]?.properties.items.map(
        item => item.id
      ) || [];

    state.viewResults = state.viewResults.filter(
      viewResult => viewResult !== resultId
    );

    state.viewItems = state.viewItems.filter(
      viewItem => !resultItems.includes(viewItem)
    );
  });

  builder.addCase(
    selectFolderViewItems,
    (state, {payload: {resultId, items}}) => {
      if (resultId && !state.viewResults.includes(resultId)) {
        state.viewResults = [...state.viewResults, resultId];
      }
      state.viewItems = [...state.viewItems, ...items];
    }
  );

  builder.addCase(
    deselectFolderViewItems,
    (state, {payload: {resultId, items}}) => {
      const newItems = state.viewItems.filter(
        viewItem => !items.includes(viewItem)
      );

      const resultItemIds =
        state.folderContentsById[resultId]?.properties.items.map(
          item => item.id
        ) ?? [];

      // check if there are ANY items from the result left
      const hasAny = resultItemIds.some(itemId => newItems.includes(itemId));
      if (!hasAny) {
        const newResults = state.viewResults.filter(
          orderResult => orderResult !== resultId
        );
        state.viewResults = newResults;
      }

      state.viewItems = newItems;
    }
  );

  builder.addCase(resetFolderSelections, state => {
    state.viewResults = defaultState.viewResults;
    state.viewItems = defaultState.viewItems;
  });
});

export default foldersReducer;
